import {
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type MutableRefObject,
    type Ref,
} from 'react';
import {
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    DOWNLOAD_ATTR_PROJECT_ID,
    DOWNLOAD_ATTR_SURFACE,
    TRACK_DOWNLOAD_ATTR,
} from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import { DownloadSurface } from 'generated/graphql';
import { button } from '~/ui-rocco/button-system';
import { LookbookCoverPreview } from '~/modules/project/lookbook/LookbookCoverPreview';
import { lookbookIntro } from '~/modules/project/lookbook/lookbook-intro';
import type { DownloadsPageLoader } from '@modules/professional/page/loaders/downloads-page-loader.server';
import {
    flyCoversOutOfStack,
    measureAll,
    settleCardsIntoStack,
    stackRotation,
    type CardPlacement,
} from './lookbook-stack-motion';

/**
 * A lookbook tile: a project's, or the professional's own, which sets its cover
 * paragraph, imprint line and caption itself.
 */
export type Lookbook = DownloadsPageLoader['lookbooks'][number] & {
    statement?: string;
    imprint?: string;
    subtitle?: string;
    /**
     * The professional's own book rather than one of their projects'. Every
     * row in the query carries the same `__typename`, and the profile's book is
     * prepended by the page, so it has to say so itself.
     */
    isProfileBook?: boolean;
};
type ElementRefs = MutableRefObject<(HTMLDivElement | null)[]>;

/** Lets the tab's control bar open and close the stack from outside the section. */
export interface LookbookTilesHandle {
    toggle: () => void;
}

interface LookbookTilesProps {
    lookbooks: Lookbook[];
    professionalID: number;
    professionalTitle: string;
    ref?: Ref<LookbookTilesHandle>;
    /** Told whenever the stack opens or closes, so the bar's control can say which. */
    onExpandedChange?: (expanded: boolean) => void;
}

/** How many covers the stack draws; the rest join when it opens. */
const STACK_LIMIT = 11;
/**
 * What a lookbook's cover prints, read the way the project page's cover reads it:
 * the hero title, and the introduction (or the description's first sentence).
 */
const coverOf = (lookbook: Lookbook, professionalTitle: string) => ({
    title: lookbook.CoverTitle,
    professional: professionalTitle,
    intro: lookbookIntro(lookbook.Subheading, lookbook.IntroLead).heading,
    imageSrc: lookbook.CoverImageUrl ?? undefined,
    statement: lookbook.statement,
    imprint: lookbook.imprint,
});

/** Hover fans the stack: the outermost cards shift this far and tilt this much more. */
const FAN_SHIFT_X_PERCENT = 18;
const FAN_DROP_Y_PERCENT = 5;
const FAN_TILT_DEG = 8;
/**
 * Each card under the top one is shaded this much darker than the card above it, up
 * to the cap, so the white covers read as separate sheets instead of running together.
 */
const DEPTH_SHADE_STEP = 0.025;
const DEPTH_SHADE_MAX = 0.15;

/**
 * A hairline contact shadow under two soft ones falling down and to the right,
 * so each cover sits on the page like a book rather than a card.
 */
const BOOK_SHADOW =
    'shadow-[0_1px_1px_0_rgba(0,0,0,0.08),1px_3px_6px_0_rgba(0,0,0,0.10),3px_8px_18px_0_rgba(0,0,0,0.10)]';

/** A card in the stack: centred, tilted, and fanned out while the stack is hovered. */
const STACK_CARD = cn(
    'absolute top-1/2 left-1/2 aspect-[595/842] w-[calc(85%_*_595_/_842)] overflow-hidden will-change-transform',
    '[box-shadow:-4px_4px_4px_0_rgba(22,30,32,0.2)]',
    '[transform:translate(-50%,-50%)_rotate(var(--stack-rot))]',
    'transition-transform duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
    'group-hover/stack:[transform:translate(calc(-50%_+_var(--stack-hover-x)),calc(-50%_+_var(--stack-hover-y)))_rotate(calc(var(--stack-rot)_+_var(--stack-hover-rot)))]',
    'motion-reduce:group-hover/stack:[transform:translate(-50%,-50%)_rotate(var(--stack-rot))]',
);

/**
 * The actions stay off the cover until it is hovered or one of them takes focus.
 * A device that cannot hover shows them outright, or they could never be reached.
 */
const REVEAL_ON_HOVER = cn(
    'opacity-0 transition-opacity duration-200 motion-reduce:transition-none',
    'group-focus-within/cover:opacity-100 group-hover/cover:opacity-100',
    '[@media(hover:none)]:opacity-100',
);

/**
 * The professional's project lookbooks, above the tab's file rows.
 *
 * They open as a stack of covers; opening it flies each cover out to its place in
 * the grid, and the grid can be stacked again. Download hands over the PDF;
 * below md the cover itself is the download.
 */
export const LookbookTiles = ({
    lookbooks,
    professionalID,
    professionalTitle,
    ref,
    onExpandedChange,
}: LookbookTilesProps) => {
    const [expanded, setExpanded] = useState(false);
    const stackCards = useRef<(HTMLDivElement | null)[]>([]);
    const gridCovers = useRef<(HTMLDivElement | null)[]>([]);
    const placements = useRef<(CardPlacement | undefined)[]>([]);
    const pendingFlight = useRef(false);
    const canStack = lookbooks.length > 1;
    const showGrid = expanded || !canStack;

    const setOpen = (open: boolean) => {
        const leaving = open ? stackCards : gridCovers;
        placements.current = measureAll(leaving.current);
        pendingFlight.current = true;
        setExpanded(open);
        onExpandedChange?.(open);
    };

    useImperativeHandle(ref, () => ({
        toggle: () => {
            if (canStack) setOpen(!expanded);
        },
    }));

    // The ref arrays are written by index, so a list that shrinks — a search
    // narrowing the books — would otherwise keep the dropped covers' slots.
    // The motion reads its own length off them, and would stagger and stack
    // against a midpoint and a depth the row no longer has.
    useLayoutEffect(() => {
        gridCovers.current.length = lookbooks.length;
        stackCards.current.length = Math.min(lookbooks.length, STACK_LIMIT);
    }, [lookbooks.length]);

    // After the swap has laid out, before paint: measured here, the covers never
    // show a frame in their new place before flying there.
    useLayoutEffect(() => {
        if (!pendingFlight.current) return;
        pendingFlight.current = false;
        if (expanded) {
            flyCoversOutOfStack(gridCovers.current, placements.current);
            return;
        }
        settleCardsIntoStack(stackCards.current, placements.current);
    }, [expanded]);

    return (
        <section aria-label="Lookbooks" className="max-md:pt-9 md:pt-6">
            {showGrid ? (
                <ul className="grid grid-cols-2 gap-x-4 gap-y-8 pt-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-x-[100px] lg:gap-y-[100px]">
                    {lookbooks.map((lookbook, index) => (
                        <LookbookTile
                            key={lookbook.ItemID}
                            lookbook={lookbook}
                            professionalID={professionalID}
                            professionalTitle={professionalTitle}
                            coverRef={(element) => {
                                gridCovers.current[index] = element;
                            }}
                        />
                    ))}
                </ul>
            ) : (
                <LookbookStack
                    lookbooks={lookbooks}
                    professionalTitle={professionalTitle}
                    cardRefs={stackCards}
                    onOpen={() => setOpen(true)}
                />
            )}
        </section>
    );
};

interface LookbookStackProps {
    lookbooks: Lookbook[];
    professionalTitle: string;
    cardRefs: ElementRefs;
    onOpen: () => void;
}

/** The first covers lying in a loose pile; the whole pile is the control that opens it. */
const LookbookStack = ({
    lookbooks,
    professionalTitle,
    cardRefs,
    onOpen,
}: LookbookStackProps) => {
    const cards = lookbooks.slice(0, STACK_LIMIT);
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen();
    };

    return (
        <div className="flex justify-center py-12">
            <div
                role="button"
                tabIndex={0}
                aria-label={`Show all ${lookbooks.length} lookbooks`}
                onClick={onOpen}
                onKeyDown={onKeyDown}
                className="group/stack relative aspect-[4/3] w-[min(768px,48%,70vh)] cursor-zoom-in outline-none focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-[#141414]"
            >
                {cards.map((lookbook, index) => (
                    <div
                        key={lookbook.ItemID}
                        ref={(element) => {
                            cardRefs.current[index] = element;
                        }}
                        className={STACK_CARD}
                        style={stackCardStyle(index, cards.length)}
                    >
                        <LookbookCoverPreview
                            {...coverOf(lookbook, professionalTitle)}
                            className="size-full"
                        />
                        {index > 0 && (
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 bg-black"
                                style={{
                                    opacity: Math.min(
                                        index * DEPTH_SHADE_STEP,
                                        DEPTH_SHADE_MAX,
                                    ),
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

/** Position -1 is the top card and +1 the bottom one; the fan spreads them that way. */
const stackCardStyle = (index: number, count: number): CSSProperties => {
    const spread = count > 1 ? (index / (count - 1)) * 2 - 1 : 0;
    return {
        '--stack-rot': `${stackRotation(index)}deg`,
        '--stack-hover-x': `${FAN_SHIFT_X_PERCENT * spread}%`,
        '--stack-hover-y': `${FAN_DROP_Y_PERCENT * Math.abs(spread)}%`,
        '--stack-hover-rot': `${FAN_TILT_DEG * spread}deg`,
        zIndex: count - index,
    } as CSSProperties;
};

interface LookbookTileProps {
    lookbook: Lookbook;
    professionalID: number;
    professionalTitle: string;
    coverRef: (element: HTMLDivElement | null) => void;
}

/**
 * One cover in the grid. Its download goes through the same gate as the lookbook on
 * the project page: signed in, a plain link to the PDF; a guest gets a button that
 * opens sign-in and hands the book back afterwards, so the PDF's address is not in
 * the page for a guest to open around the gate.
 */
const LookbookTile = ({
    lookbook,
    professionalID,
    professionalTitle,
    coverRef,
}: LookbookTileProps) => {
    const cover = coverOf(lookbook, professionalTitle);
    // The stack leads with the professional's own book, whose `ItemID` is the
    // professional rather than a project of theirs. Reporting it as a
    // ProjectLookbook filed a professional's id under `projectID`; it is a file
    // of the profile's, which is the surface that says so.
    const isProfileBook = lookbook.isProfileBook === true;
    const surface = isProfileBook
        ? DownloadSurface.ProfileDownloads
        : DownloadSurface.ProjectLookbook;
    const { isLoggedIn, handleGatedDownload } = useGatedDownloads({
        surface,
        // The profile book's `ItemID` is the professional, not a page of
        // theirs, and a file filed under the professional has no library item.
        // Passing it anyway put a professional's id in `productID` — the same
        // mis-filing this rail's surface fix set out to remove.
        ...(isProfileBook ? {} : { libraryItemID: lookbook.ItemID }),
        professionalID,
        itemImage: cover.imageSrc,
        lookbookArtwork: cover,
    });
    const actionClass = button('primary-inverse', 's');
    const downloadLabel = `Download the ${lookbook.Title} lookbook`;
    // Logged by the delegated download-link listener, the way every other
    // download anchor is.
    const trackingAttributes = {
        [TRACK_DOWNLOAD_ATTR]: '1',
        // Names the book as a book. Without it a lookbook taken by an
        // entitled reader is indistinguishable from any other file.
        [DOWNLOAD_ATTR_SURFACE]: surface,
        ...(isProfileBook
            ? {}
            : { [DOWNLOAD_ATTR_PROJECT_ID]: lookbook.ItemID }),
        [DOWNLOAD_ATTR_FILE_TITLE]: lookbook.Title,
        [DOWNLOAD_ATTR_PROFESSIONAL_ID]: professionalID,
        [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]: professionalTitle,
    };

    return (
        <li className="flex min-w-0 flex-col">
            <div ref={coverRef} className="group/cover relative">
                <LookbookCoverPreview
                    {...cover}
                    className={cn('w-full', BOOK_SHADOW)}
                />
                {/* Below md the cover is the download: a tap hands over the
                    PDF, through the same gate, with no controls on the
                    picture. */}
                {isLoggedIn ? (
                    <a
                        href={lookbook.LookbookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download=""
                        aria-label={downloadLabel}
                        className="absolute inset-0 md:hidden"
                        {...trackingAttributes}
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() =>
                            handleGatedDownload(lookbook.LookbookUrl)
                        }
                        aria-label={downloadLabel}
                        className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0 md:hidden"
                    />
                )}
                <div
                    className={cn(
                        'absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/10 max-md:hidden',
                        REVEAL_ON_HOVER,
                    )}
                >
                    {isLoggedIn ? (
                        <a
                            href={lookbook.LookbookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download=""
                            aria-label={downloadLabel}
                            className={cn(
                                actionClass,
                                'no-underline hover:no-underline',
                            )}
                            {...trackingAttributes}
                        >
                            Download
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={() =>
                                handleGatedDownload(
                                    lookbook.LookbookUrl,
                                    surface,
                                    // Named, or the gate falls back to the
                                    // surface as the file's title and every
                                    // book reports as "ProjectLookbook".
                                    {
                                        title: lookbook.Title,
                                        professionalTitle,
                                    },
                                )
                            }
                            aria-label={downloadLabel}
                            className={actionClass}
                        >
                            Download
                        </button>
                    )}
                </div>
            </div>
            {/* The project tile's light browse caption, so a lookbook reads like
                the project it belongs to on the Projects tab. */}
            <div className="flex min-w-0 flex-col gap-0.5 pt-3 font-sans">
                <span
                    className="block overflow-hidden font-medium text-nowrap text-ellipsis text-[#323232] max-md:text-[0.75rem] max-md:leading-[1.2] md:text-[1rem] md:leading-[1.3]"
                    title={lookbook.Title}
                >
                    {lookbook.Title}
                </span>
                <span className="block overflow-hidden font-normal text-nowrap text-ellipsis text-[#323232]/40 max-md:text-[0.75rem] max-md:leading-[1.3] md:text-[1rem] md:leading-[1.3]">
                    {lookbook.subtitle ?? professionalTitle}
                </span>
            </div>
        </li>
    );
};
